import { ReactElement, useCallback, useMemo, forwardRef, memo } from 'react';
import BootstrapTable, {
  BootstrapTableProps,
} from 'react-bootstrap-table-next';
import overlayFactory from 'react-bootstrap-table2-overlay';
import LoadingOverlay from 'react-loading-overlay';
import { isEqual } from 'lodash';
import classNames from 'classnames';

import { TableRef, TableProps } from './types';

import ColumnToggleProvider from './ColumnToggleContext';
import NoRecordsFoundMessage from './NoRecordsFoundMessage';
import Pagination from './Pagination';
import DoubleScrollHorizontal from './DoubleScrollHorizontal';

import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-bootstrap-table-next/dist/react-bootstrap-table2.css';
import 'react-bootstrap-table2-paginator/dist/react-bootstrap-table2-paginator.css';

LoadingOverlay.propTypes = undefined;

const defaultRemoteObject: BootstrapTableProps['remote'] = {
  pagination: true,
};

const DataTable = forwardRef<TableRef, TableProps>(function DataTable(
  inProps,
  ref,
): ReactElement {
  const {
    loading = true,
    data = [],
    columns = [],
    keyField,
    pagination,
    remote: remoteProps,
    onTableChange: onTableChangeProps,
    noDataIndicationComponent: NoDataIndicationComponent,
    toggleHideableColumns = false,
    excludeHideableFieldColumns = [],
    hideableStorageKey,
    wrapperClasses,
    classes,
    ...restProps
  } = inProps;

  const getNoDataIndication = useCallback(
    () =>
      loading ? (
        <div />
      ) : NoDataIndicationComponent ? (
        <NoDataIndicationComponent />
      ) : (
        <NoRecordsFoundMessage />
      ),
    [loading, NoDataIndicationComponent],
  );

  const remote = useMemo<BootstrapTableProps['remote']>(() => {
    if (!pagination) return false;

    return {
      ...defaultRemoteObject,
      ...remoteProps,
    };
  }, [pagination, remoteProps]);

  const onTableChange = useMemo<BootstrapTableProps['onTableChange']>(
    () => (!pagination ? undefined : onTableChangeProps),
    [pagination, onTableChangeProps],
  );

  return (
    <ColumnToggleProvider
      keyField={keyField}
      bootstrap4
      data={data}
      columns={columns}
      toggleHideableColumns={toggleHideableColumns}
      excludeHideableFieldColumns={excludeHideableFieldColumns}
      hideableStorageKey={hideableStorageKey}
    >
      {(tableProps) => (
        <DoubleScrollHorizontal
          data={data}
          loading={loading}
          pagination={pagination}
        >
          <Pagination pagination={pagination}>
            <BootstrapTable
              {...tableProps}
              // @ts-expect-error: Let's ignore a compile error like this unreachable code
              ref={ref}
              loading={loading}
              overlay={overlayFactory({
                spinner: true,
                styles: {
                  overlay: (base: Record<string, unknown>) => ({
                    ...base,
                    background: 'rgba(0, 0, 0, 0.2)',
                    zIndex: 1,
                  }),
                  spinner: (base: Record<string, unknown>) => ({
                    ...base,
                    '& svg circle': {
                      stroke: '#5618b6',
                      strokeWidth: 4,
                    },
                  }),
                },
              })}
              noDataIndication={getNoDataIndication}
              wrapperClasses={classNames(
                'table-responsive',
                'wrapper-data-table-content-id',
                wrapperClasses,
              )}
              classes={classNames(
                'table',
                'table-head-custom',
                'table-vertical-center',
                'overflow-hidden',
                classes,
              )}
              bordered={false}
              hover={!loading && tableProps.data.length > 0}
              onTableChange={onTableChange}
              remote={remote}
              {...restProps}
            />
          </Pagination>
        </DoubleScrollHorizontal>
      )}
    </ColumnToggleProvider>
  );
});

const propsAreEqual = (
  prevProps: Readonly<TableProps>,
  nextProps: Readonly<TableProps>,
): boolean =>
  prevProps.loading === nextProps.loading &&
  prevProps.data.length === nextProps.data.length &&
  prevProps.columns.length === nextProps.columns.length &&
  isEqual(prevProps.selectRow, nextProps.selectRow) &&
  isEqual(prevProps.expandRow, nextProps.expandRow);

export default memo(DataTable, propsAreEqual);
